import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { getBeneProviders } from "../../../api";
import { StaleTime } from "../types";
import type { MctProvider } from "../../../@types";
import { useLoggedIn } from "../../loginHelpers";
import { useBeneinfoProvidersQueryKey } from "../queryKeyHooks";
import { useMemo } from "react";
import { useMctFlags } from "../../flag-hooks/useMctFlags";
import { useAppContext } from "../../context-hooks/useAppContext";
import { useMapnetCoverage } from "../../flag-hooks/useMapnetCoverage";

/**
 * Fetch a logged-in bene's saved providers
 *
 * Synchronizes with `AppState.providers`, since that's the context for an
 * anonymous session's provider list
 *
 * RQ hook values are returned, if needed, but generally should just be called, `useBeneinfoProviders()`
 */
export const useBeneinfoProviders = ({
  disabled = false,
}: {
  disabled?: boolean;
} = {}): Omit<Partial<UseQueryResult<MctProvider[], Error>>, "data"> & {
  providers: MctProvider[];
} => {
  const { tmpFeEnableProviderNetwork } = useMctFlags();
  const { state } = useAppContext();
  const { enableMapnetCoverage } = useMapnetCoverage(state.year);
  const beneinfoProvidersQueryKey = useBeneinfoProvidersQueryKey();
  const isLoggedIn = useLoggedIn();
  const isLegacyQueryDisabled = disabled || enableMapnetCoverage;

  // * Queries
  const { data, error, isError, isLoading } = useQuery({
    enabled:
      tmpFeEnableProviderNetwork &&
      !!state.year &&
      isLoggedIn &&
      !isLegacyQueryDisabled,
    queryKey: beneinfoProvidersQueryKey,
    queryFn: () => getBeneProviders(),
    staleTime: StaleTime.Infinite,
  });

  const providers = useMemo(() => data ?? [], [data]);

  return {
    error,
    isError,
    isLoading,
    providers,
  };
};
